import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BehaviorSubject } from "rxjs";
import { Priority } from "../../../model/priority";
import { Messages } from "../../../model/messages";
import { PrioritiesService } from "../../../services/priorities.service";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Navigation } from "../../../model/navigation";
import { ActionButtonSubmit } from "../../../model/action-button-submit";

@Component({
  selector: "priority-details",
  templateUrl: "./priority-details.component.html",
  styleUrls: ["./priority-details.component.css"],
})
export class PriorityDetailsComponent implements OnInit {
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  priority = new BehaviorSubject<Priority>(new Priority(0, "", "", 2));
  editMode: boolean = false;
  priorityForm: FormGroup;
  priorityTitle: string = "priorityDetails.title.exist.priority";
  isLoading: boolean = false;
  supervisor: boolean = false;
  constructor(
    private priorityService: PrioritiesService,
    private auth: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private nav: NavigationHelper
  ) {}

  ngOnInit(): void {
    this.initializeForm();

    if (Navigation.NEW.toString() === this.nav.currentId) {
      this.editMode = true;
      this.priorityForm.reset();
      this.priorityTitle = "priorityDetails.title.new.priority";
    }
    this.priorityService.selectedPriorityObservable.subscribe(
      (selectedPriority) => {
        if (selectedPriority) {
          this.messages.next(Messages.EMPTY);
          this.populateForm(selectedPriority);
        }
      }
    );
    if (
      !(
        this.priorityService.currentPriority &&
        this.priorityService.currentPriority.id
      )
    ) {
      this.priorityService.selectFromRouter();
    }
    this.supervisor = this.auth.isSupervisor();
  }

  private initializeForm(): void {
    this.priorityForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      color: [""],
      importance: [1, Validators.required],
    });
  }

  private populateForm(priority: Priority): void {
    this.priority.next(priority);
    this.priorityForm.patchValue({
      id: priority.id,
      name: priority.value,
      color: priority.color,
      importance: priority.importance,
    });
    this.editMode = priority.id === Navigation.NEW;
    this.priorityTitle = this.editMode
      ? "priorityDetails.title.new.priority"
      : "priorityDetails.title.exist.priority";
  }

  toggleEditMode() {
    if (this.editMode) {
      this.editMode = false;
      this.priority.next(Priority.EMPTY);
      this.priorityForm.reset();
      this.messages.next(Messages.EMPTY);
      this.priorityTitle = "priorityDetails.title.exist.priority";
    } else {
      this.editMode = true;
    }
  }

  savePriority(action: ActionButtonSubmit): void {
    this.isLoading = true;

    if (this.priorityForm.valid) {
      const token = this.auth.token;
      const id = this.priority.value.id;

      const updatedPriority = new Priority(
        id === null || id < 1 ? null : id,
        this.priorityForm.value.name || "",
        this.priorityForm.value.color || "",
        this.priorityForm.value.importance
      );

      if (updatedPriority.id === null || updatedPriority.id < 1) {
        this.priorityService.createPriority(token, updatedPriority).subscribe(
          (messages) => {
            this.isLoading = false;
            this.messages.next(messages);

            if (messages.hasAnyMessages) {
              action.result(false);
            } else {
              this.updatePriorityList(token);
              this.router.navigate(["priority"]);
              action.result(true);
            }
          },
          (error) => {
            this.isLoading = false;
            action.result(false);
            this.messages.next(
              Messages.from({ priority: ["priority.unknown.error", error] })
            );
          }
        );
      } else {
        this.priorityService.updatePriority(token, updatedPriority).subscribe(
          (messages) => {
            this.isLoading = false;
            this.messages.next(messages);

            if (messages.hasAnyMessages) {
              action.result(false);
            } else {
              this.updatePriorityList(token);
              this.router.navigate(["priority"]);
              action.result(true);
            }
          },
          (error) => {
            this.isLoading = false;
            action.result(false);
            this.messages.next(
              Messages.from({ priority: ["priority.unknown.error", error] })
            );
          }
        );
      }
    } else {
      this.isLoading = false;
      action.result(false);

      this.messages.next(
        Messages.from({ priority: ["priority.form.invalid"] })
      );
    }
  }

  deletePriority(): void {
    const token = this.auth.token;
    const priorityId = this.priority.value.id;

    if (priorityId != null) {
      this.isLoading = true;
      this.priorityService.deletePriority(token, priorityId).subscribe(
        (response) => {
          this.messages.next(response);
          this.updatePriorityList(token);
          this.priority.next(Priority.EMPTY);
          this.isLoading = false;
        },
        (error) => {
          this.messages.next(
            Messages.from({ priority: ["priority.delete.failed"] })
          );
          this.isLoading = false;
        }
      );
    }
  }

  updatePriorityList(token: string): void {
    this.priorityService.priorities(token).subscribe((messages) => {
      const updatedPriority = this.priorityService.prioritiesData;
      this.priorityService._currentPriorities.next(updatedPriority);
    });
  }
  get TRUE(): boolean {
    return true;
  }
  get FALSE(): boolean {
    return false;
  }
  get isSupervisor(): boolean {
    return this.auth.userdata.supervisor;
  }
}
